import React from 'react';
import {PathUtility} from 'src/shared/constants/PathBuilder';

import type {WorkflowSimpleResponse} from 'src/api/ProactApiClient';
import {ContentWrapper} from 'src/app/layout/ContentWrapper';
import {DataHeader} from 'src/design/organisms/DataHeader';
import {DataList} from 'src/design/organisms/DataList';
import {useListState} from 'src/design/organisms/useListState';
import useDebounce from 'src/shared/hooks/useDebounce';
import useSetDocumentTitle from 'src/shared/hooks/useSetDocumentTitle';
import {useGetWorkflowListQuery} from 'src/redux/queries/workflow';
import {
  WorkflowDocumentTitle,
  WorkflowHistoryPillConfig,
} from './workflowHelpers';
import {useAppNavigate} from 'src/shared/hooks/useAppNavigate';

const maxPageRows = 10;

export interface ViewWorkflowHistoryProps {
  definitionId?: string;
}
export const ViewWorkflowHistory: React.FC<ViewWorkflowHistoryProps> = ({
  definitionId,
}) => {
  useSetDocumentTitle(WorkflowDocumentTitle);
  const navigate = useAppNavigate();
  const {
    searchTerm,
    setSearchTerm,
    currentPage,
    handlePageChange,
    start,
    length,
  } = useListState({
    identifier: `workflow-history-${definitionId ?? 'none'}`,
    pageSize: maxPageRows,
  });
  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  const parsedDefinitionId = definitionId !== undefined ? parseInt(definitionId) : undefined;

  const {data, isFetching: isLoading} = useGetWorkflowListQuery(
    {
      start,
      length,
      search: debouncedSearchTerm,
      sortColumn: 'id',
      sortDirection: 'desc',
      workflowDefinitionId: parsedDefinitionId,
    },
    {skip: definitionId === undefined}
  );

  const indexListData = data?.data ?? [];
  const totalRecords = data?.totalRecords ?? 0;

  const handleNavigateTo = (id: string | number) =>
    navigate(PathUtility.WorkflowView(definitionId as string, id as string));

  const handleSearchTerm = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const {value} = e.target;
    setSearchTerm(value);
  };

  return (
    <ContentWrapper>
      <DataHeader
        titleText="History"
        searchTerm={searchTerm}
        onSearchTermChange={handleSearchTerm}
        isFilterActive={false}
      />
      <DataList
        isLoading={isLoading}
        data={indexListData}
        currentPage={currentPage}
        totalRecords={totalRecords}
        recordsPerPage={maxPageRows}
        onRowClick={handleNavigateTo}
        onPageChange={handlePageChange}
        onGetPillMapping={(item: WorkflowSimpleResponse) =>
          WorkflowHistoryPillConfig(item.state)
        }
        onRowTextRender={(item: WorkflowSimpleResponse) =>
          item.internalIdentifier ?? 'Invalid Identifier'
        }
        hasSearchTerm={searchTerm !== ''}
        hasCreationLogic={false}
        meta={undefined}
      />
    </ContentWrapper>
  );
};

export default ViewWorkflowHistory;
